import { useId, useState } from 'react';

import Button from '../../../common/components/button/button';
import Sheet from '../../../common/components/sheet/sheet';
import { DECISION_REASON_MAX, DECISION_RESULT } from '../../../core/constants/domain';
import { dataSource } from '../../../global/config/data-source';
import { useNotice } from '../../../global/contexts/notice/notice';
import { useDecideReview } from '../contexts/reviews';
import { describeDecisionError, validateDecision } from '../utils/decision';

import '../styles/decision.css';

const COPY = {
    [DECISION_RESULT.APPROVED]: {
        title: 'Aceptar la entrega',
        confirm: 'Confirmar aceptación',
        reasonLabel: 'Comentario (opcional)',
        placeholder: 'Algo que el equipo deba saber al cerrar la tarea.',
    },
    [DECISION_RESULT.RETURNED]: {
        title: 'Devolver la entrega',
        confirm: 'Confirmar devolución',
        reasonLabel: 'Motivo de la devolución',
        placeholder: 'Qué criterio falla y qué se espera para la próxima ronda.',
    },
};

export default function DecisionForm({ taskId, round, result, onClose, onDecided }) {
    const reasonId = useId();
    const errorId = useId();
    const [reason, setReason] = useState('');
    const [fieldError, setFieldError] = useState(null);
    const { notify } = useNotice();
    const decide = useDecideReview(taskId);
    const copy = COPY[result];

    const serverError = decide.error?.fields?.reason || null;
    const reasonError = fieldError || serverError;

    const handleSubmit = async (event) => {
        event.preventDefault();
        const checked = validateDecision({ result, reason });
        if (!checked.isValid) {
            setFieldError(checked.errors.reason || checked.errors.result);
            return;
        }
        setFieldError(null);
        try {
            const response = await decide.mutateAsync({ roundId: round.id, result, reason: checked.reason });
            const message = dataSource.isDemo
                ? 'Decisión registrada en la demostración. Producción no cambió.'
                : 'Decisión enviada a Core.';
            notify({ tone: 'success', message });
            onDecided({ result, response });
        } catch {
            // The mutation error is rendered inline below; nothing else to do.
        }
    };

    return (
        <Sheet
            isOpen
            title={copy.title}
            description={`Ronda ${round.number} · ${round.summary.cumple} cumplen, ${round.summary.incumple} incumplen, ${round.summary.no_comprobado} sin comprobar.`}
            onClose={onClose}
        >
            <form className="decision-form" onSubmit={handleSubmit} noValidate>
                <p className={`decision-form__mode decision-form__mode--${dataSource.isDemo ? 'demo' : 'api'}`}>
                    {dataSource.isDemo
                        ? 'Modo demostración: la decisión solo cambia los datos ficticios de esta sesión.'
                        : 'Se enviará a Core. Requiere permiso tasks:review y rol QA o lead en el proyecto.'}
                </p>
                <label htmlFor={reasonId} className="decision-form__label">{copy.reasonLabel}</label>
                <textarea
                    id={reasonId}
                    className="decision-form__reason"
                    rows={5}
                    maxLength={DECISION_REASON_MAX}
                    value={reason}
                    placeholder={copy.placeholder}
                    aria-invalid={!!reasonError}
                    aria-describedby={reasonError ? errorId : undefined}
                    required={result === DECISION_RESULT.RETURNED}
                    onChange={(event) => setReason(event.target.value)}
                />
                <p className="decision-form__counter">{reason.length}/{DECISION_REASON_MAX}</p>
                {reasonError && <p id={errorId} className="decision-form__error" role="alert">{reasonError}</p>}
                {decide.isError && !serverError && (
                    <p className="decision-form__error" role="alert">{describeDecisionError(decide.error)}</p>
                )}
                <div className="decision-form__actions">
                    <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant={result === DECISION_RESULT.APPROVED ? 'positive' : 'danger'}
                        isBusy={decide.isPending}
                    >
                        {decide.isPending ? 'Enviando…' : copy.confirm}
                    </Button>
                </div>
            </form>
        </Sheet>
    );
}
